import { ShoppingListItem } from './ShoppingListItem';
import { ChevronDown } from 'lucide-react';
import { getCategoryConfig } from '../utils/categoryConfig';

export function ShoppingListCategory({ 
  title, 
  items, 
  onToggleItem, 
  onDeleteItem, 
  onEditItem, 
  onMoveItem, 
  onReorderItems, 
  onMoveItemToCategory,
  isReorderMode,
  customCategoryIndex,
  isExpanded,
  onToggleExpand
}) {
  const categoryConfig = getCategoryConfig(title, customCategoryIndex);
  const CategoryIcon = categoryConfig.icon;
  const categoryColor = categoryConfig.color || 'var(--primary)';
  
  if (items.length === 0) return null;

  // Sort items: unchecked first; recently checked with pendingUntil stay in place until delay completes
  const sortedItems = [...items].sort((a, b) => {
    // If reorder mode is active, don't sort - keep manual order
    if (isReorderMode) return 0;
    const now = Date.now();
    const aPending = a.pendingUntil && a.pendingUntil > now;
    const bPending = b.pendingUntil && b.pendingUntil > now;
    const aChecked = a.checked && !aPending;
    const bChecked = b.checked && !bPending;

    if (aChecked === bChecked) return 0;
    return aChecked ? 1 : -1;
  });

  // Count how many items are checked/unchecked for visual separator
  const now = Date.now();
  const uncheckedCount = items.filter(item => {
    const pending = item.pendingUntil && item.pendingUntil > now;
    return !(item.checked && !pending);
  }).length;
  const checkedCount = items.filter(item => {
    const pending = item.pendingUntil && item.pendingUntil > now;
    return item.checked && !pending;
  }).length;

  const handleDragStart = (e, item) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('itemId', item.id);
    e.dataTransfer.setData('fromCategory', title);
  };

  const handleItemDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    const itemId = e.dataTransfer.getData('itemId');
    const fromCategory = e.dataTransfer.getData('fromCategory');
    
    if (fromCategory === title) {
      // Same category - reorder within category
      const draggedItemIndex = items.findIndex(item => item.id === itemId);
      if (draggedItemIndex === -1) return;
      
      const newItems = [...items];
      const [draggedItem] = newItems.splice(draggedItemIndex, 1);
      
      // Adjust target index if dragging downward within same category
      let adjustedIndex = targetIndex;
      if (draggedItemIndex < targetIndex) {
        adjustedIndex = targetIndex - 1;
      }
      
      newItems.splice(adjustedIndex, 0, draggedItem);
      onReorderItems(title, newItems);
    } else {
      // Different category - move to specific position
      if (onMoveItemToCategory) {
        onMoveItemToCategory(itemId, fromCategory, title, targetIndex);
      }
    }
  };

  return (
    <div className="mb-6">
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between text-muted-foreground mb-3 px-1 py-1 active:opacity-60 transition-opacity"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title} category`}
      >
        <div className="flex items-center gap-2">
          <CategoryIcon className="w-5 h-5" color={categoryColor} />
          <h3 className="text-base font-medium text-muted-foreground">{title}</h3>
        </div>
        <ChevronDown 
          className={`w-5 h-5 transition-transform duration-200 ${
            isExpanded ? '' : '-rotate-90'
          }`}
        />
      </button>
      
      {isExpanded && (
        <div className="space-y-2">
        {sortedItems.map((item, index) => (
            <div key={item.id}>
              {/* Show divider between unchecked and checked items */}
              {!isReorderMode && index === uncheckedCount && uncheckedCount > 0 && checkedCount > 0 && (
                <div className="flex items-center gap-3 py-3 px-2">
                  <div className="flex-1 h-px bg-[var(--border)]" />
                  <span className="text-xs text-muted-foreground">
                    {checkedCount} completed
                  </span>
                  <div className="flex-1 h-px bg-[var(--border)]" />
                </div>
              )}
              <ShoppingListItem
                key={item.id}
                id={item.id}
                name={item.name}
                quantity={item.quantity}
                unit={item.unit}
                checked={item.checked}
                onToggle={onToggleItem}
                onDelete={onDeleteItem}
                onEdit={onEditItem}
                onMove={onMoveItem}
                isReorderMode={isReorderMode}
                index={index}
                totalItems={items.length}
                onDragStart={handleDragStart}
                onDrop={handleItemDrop}
                categoryColor={categoryColor}
                categoryName={title}
                isFirstInCategory={index === 0}
                isLastInCategory={index === items.length - 1}
              />
            </div>
          ))}

        </div>
      )}
    </div>
  );
}
